import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { RxCross2 } from "react-icons/rx";

type LoginInputType = "text" | "password" | "email";

type Props = {
  type?: LoginInputType;
  value?: string;
  onChange?: (text: string) => void;
  onKeyDownEnter?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  autoComplete?: string;
  placeholder?: string;
  error?: string;
  className?: string;
  outRef?: React.RefObject<HTMLInputElement>;
};

export default function LoginInput({
  value,
  onChange,
  onKeyDownEnter,
  autoComplete,
  placeholder,
  error,
  className,
  outRef,
  type = "text",
}: Props) {
  const [inputType, setInputType] = useState<LoginInputType>(type);
  const [focus, setFocus] = useState<boolean>(false);
  const innerInputRef = useRef<HTMLInputElement>(null);
  const inputRef = outRef ?? innerInputRef;
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event.target.value);
    }
  };

  const handleClearButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    if (onChange) {
      onChange("");
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();

    if (event.key === "Enter") {
      event.preventDefault();
      if (onKeyDownEnter) {
        onKeyDownEnter(event);
      }
    }
  };

  const handleTogglePasswordVisibility = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setInputType((prev) => (prev === "text" ? "password" : "text"));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        if (inputRef.current) {
          inputRef.current.blur();
          setFocus(false);
        }
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  return (
    <div>
      <div className="relative" ref={wrapperRef}>
        <input
          type={inputType}
          className={clsx([
            "border w-full p-4 hover:border-black hover:border-b transition-all focus:outline-none",
            className,
            { "border-black": focus },
            { "border-brand-primary-magenta": error },
          ])}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          ref={inputRef}
          autoComplete={autoComplete}
          onFocus={() => setFocus(true)}
          placeholder={placeholder}
        />
        {focus && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2 flex gap-1 z-10">
            {type === "password" && (
              <button
                className={clsx([
                  "w-5 h-5 rounded-full text-white flex items-center justify-center cursor-pointer hover:brightness-90 text-xl",
                  { "text-black": inputType === "text" },
                  { "text-neutral-400": inputType === "password" },
                ])}
                onClick={handleTogglePasswordVisibility}
              >
                {inputType === "text" ? (
                  <LoginInputOpenEyeIcon />
                ) : (
                  <LoginInputCloseEyeIcon />
                )}
              </button>
            )}
            {value && value.length > 0 && (
              <button
                className="w-5 h-5 rounded-full bg-neutral-400 text-white flex items-center justify-center cursor-pointer hover:brightness-90"
                onClick={handleClearButtonClick}
                aria-label="Clear input value"
              >
                <RxCross2 />
              </button>
            )}
          </div>
        )}
      </div>
      {error && (
        <div
          className="px-4 pt-1 text-brand-primary-magenta text-xs"
          aria-label="errorMessage"
        >
          {error}
        </div>
      )}
    </div>
  );
}

export const LoginInputOpenEyeIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.2576 13.7265C15.2576 15.4725 13.8416 16.8875 12.0956 16.8875C10.3496 16.8875 8.93457 15.4725 8.93457 13.7265C8.93457 11.9795 10.3496 10.5645 12.0956 10.5645C13.8416 10.5645 15.2576 11.9795 15.2576 13.7265Z"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
    <path
      d="M21.3467 13.7257C19.3857 9.59758 15.9027 7.12109 12.0947 7.12109H12.0987C8.29068 7.12109 4.80768 9.59758 2.84668 13.7257"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
  </svg>
);

export const LoginInputCloseEyeIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M21.5 7.01066C19.539 11.1387 16.056 13.6152 12.248 13.6152H12.252C8.444 13.6152 4.961 11.1387 3 7.01066"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
    <path
      d="M18.8398 10.8789L20.951 12.99"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
    <path
      d="M5.5813 10.8789L3.47019 12.99"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
    <path
      d="M15.4268 16.1967L14.5823 13.2871"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
    <path
      d="M8.99338 16.1967L9.83789 13.2871"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
  </svg>
);
